import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  deleteEvent,
  duplicateEvent,
  getEventPage,
  getOngoingEvents
} from '../../features/event/eventSlice';
import {
  Typography,
  Box,
  CircularProgress,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Fab
} from '@mui/material';
import PrimaryButton from '../ui/buttons/primaryButton';
import AddEditEventDialog from './addEditEventDialog';

import { Dialog } from '@mui/material';
import { Add } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTitle } from '../../hooks/titleProvider';
import ImportEventDialog from './importEventDialog';

import { useAuth } from '../../contexts/authContext';
import { Roles, hasWorkspaceAccess } from '../../helpers/access';
import EventsTable from '../ui/table/eventTable';
import EventPagination from './eventPagination';

const PAGE_SIZE = 10;
const EVENT_SCOPE = 'upcoming';

const Events = () => {
  const dispatch = useDispatch();
  const eventPage = useSelector((state) => state.event.eventPage);
  const eventPageScope = useSelector((state) => state.event.eventPageScope);
  const eventPageLoading = useSelector((state) => state.event.eventPageLoading);
  const [page, setPage] = useState(0);
  const [refreshToken, setRefreshToken] = useState(0);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [importEvent, setImportEvent] = useState(null);
  const [duplicatingEventId, setDuplicatingEventId] = useState(null);
  const navigate = useNavigate();
  const { currentUserProfile, currentUser, currentWorkspace } = useAuth();
  const { setTitle } = useTitle();
  setTitle('Events');

  const pageReady = eventPageScope === EVENT_SCOPE && !eventPageLoading;

  const workspaceMemberRole =
    currentWorkspace?.members?.find((member) => member?.id === currentUser?.uid)?.role || '';
  const isWorkspaceAdmin =
    String(workspaceMemberRole).toLowerCase() === 'admin' ||
    currentWorkspace?.ownerId === currentUser?.uid;
  const canManageEvents = hasWorkspaceAccess(currentUserProfile, currentWorkspace, Roles.ADMIN) || isWorkspaceAdmin;

  useEffect(() => {
    dispatch(getEventPage({ scope: EVENT_SCOPE, limit: PAGE_SIZE, offset: page * PAGE_SIZE }));
  }, [dispatch, page, currentWorkspace?.id, refreshToken]);

  // A different workspace has a different list, so start it at page one.
  useEffect(() => {
    setPage(0);
  }, [currentWorkspace?.id]);

  // An edit can move the last event on this page out of the list; step back
  // instead of sitting on an empty page.
  useEffect(() => {
    if (pageReady && page > 0 && eventPage.events.length === 0) {
      setPage((currentPage) => Math.max(0, currentPage - 1));
    }
  }, [pageReady, page, eventPage.events.length]);

  const refreshEvents = () => {
    setRefreshToken((token) => token + 1);
    dispatch(getOngoingEvents());
  };

  const handleEditOpen = async (event) => {
    setSelectedEvent(event);
    setOpenEdit(true);
  };

  const handleEditClose = async () => {
    setSelectedEvent(null);
    setOpenEdit(false);
    refreshEvents();
  };

  const handleDeleteClose = async () => {
    setOpenDelete(false);
  };

  const handleDeleteOpen = async (event) => {
    setSelectedEvent(event);
    setOpenDelete(true);
  };

  const handleDuplicate = async (event) => {
    try {
      setDuplicatingEventId(event.id);
      const duplicatedEvent = await dispatch(duplicateEvent({ eventId: event.id })).unwrap();
      navigate(`/events/${duplicatedEvent.id}`);
    } catch (error) {
      console.error('Failed to duplicate event:', error);
    } finally {
      setDuplicatingEventId(null);
    }
  };

  const handleConfirm = async () => {
    const removingOnlyRow = eventPage.events.length === 1 && page > 0;
    if (selectedEvent) {
      await dispatch(deleteEvent(selectedEvent.id));
    }

    setOpenDelete(false);
    if (removingOnlyRow) {
      setPage((currentPage) => currentPage - 1);
      dispatch(getOngoingEvents());
    } else {
      refreshEvents();
    }
  };

  const handleImport = (clubMasterEvent) => {
    setImportEvent(clubMasterEvent);
    setOpenEdit(true);
  };

  return (
    <>
      <Box
        display={'flex'}
        flexDirection={'row'}
        justifyContent={'space-between'}
        alignItems={'center'}
        mb={2}>
        <Box display={'flex'} flexDirection={'row'} alignItems={'center'}>
          <Button variant="outlined" onClick={() => navigate('/events/archive')} sx={{ ml: 2 }}>
            Past Events
          </Button>
        </Box>
        {canManageEvents && (
          <Box>
            <Fab size="small" color="primary" aria-label="add" onClick={() => handleEditOpen()}>
              <Add />
            </Fab>
            <ImportEventDialog onImport={(clubMasterEvent) => handleImport(clubMasterEvent)} />
          </Box>
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column'
        }}>
        {selectedEvent ? (
          <AddEditEventDialog
            open={openEdit}
            onClose={() => handleEditClose()}
            event={selectedEvent}
            importedEvent={importEvent}
          />
        ) : (
          <AddEditEventDialog
            open={openEdit}
            onClose={() => handleEditClose()}
            importedEvent={importEvent}
          />
        )}

        {!pageReady ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height="100%">
            <CircularProgress />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Loading events...
            </Typography>
          </Box>
        ) : (
          <>
            <EventsTable
              events={eventPage.events}
              onEdit={(event) => handleEditOpen(event)}
              onDuplicate={
                hasWorkspaceAccess(currentUserProfile, currentWorkspace, Roles.SUPERUSER)
                  ? (event) => handleDuplicate(event)
                  : undefined
              }
              duplicateEventId={duplicatingEventId}
              onDelete={(event) => handleDeleteOpen(event)}
            />
            <EventPagination
              count={eventPage.total}
              page={page}
              rowsPerPage={PAGE_SIZE}
              onPageChange={setPage}
            />
          </>
        )}
      </Box>

      <Dialog
        open={openDelete}
        onClose={handleDeleteClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description">
        <DialogTitle id="alert-dialog-title">{selectedEvent ? selectedEvent.name : ''}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this event? It will be hidden from your events and can
            be brought back by the assistant.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <PrimaryButton variant="contained" onClick={handleDeleteClose}>
            Cancel
          </PrimaryButton>
          <PrimaryButton onClick={handleConfirm} autoFocus>
            Confirm
          </PrimaryButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Events;
